import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, Globe, Search } from 'lucide-react'
import { api } from '@/lib/api'
import type { SeoConfig } from '@shared/seo.config'
import { Seo } from '@/components/Seo'
import { PageHeader } from '@/components/Page'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/sonner'
import { useI18n } from '@/i18n'

export default function Settings() {
  const { t } = useI18n()
  return (
    <>
      <Seo title={t('settings.title')} noIndex />
      <PageHeader
        eyebrow={t('settings.eyebrow')}
        title={t('settings.title')}
        body={t('settings.body')}
      />

      <div data-upbo-src="src/pages/Settings.tsx:31" className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <Tabs defaultValue="seo" className="mt-8">
          <TabsList>
            <TabsTrigger value="seo"><Search className="size-3.5" />{t('settings.tab.seo')}</TabsTrigger>
            <TabsTrigger value="ai"><Bot className="size-3.5" />{t('settings.tab.ai')}</TabsTrigger>
          </TabsList>
          <SeoSettings />
        </Tabs>
      </div>
    </>
  )
}

function SeoSettings() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: ['seo'], queryFn: api.seo.get })
  const [form, setForm] = useState<SeoConfig | null>(null)

  // Load the form from the server ONCE. Re-syncing on every query change would
  // overwrite whatever the user is halfway through typing on a background
  // refetch.
  useEffect(() => { if (data && !form) setForm(data) }, [data, form])

  const save = useMutation({
    mutationFn: (patch: Partial<SeoConfig>) => api.seo.save(patch),
    onSuccess: (saved) => {
      setForm(saved)
      qc.setQueryData(['seo'], saved)
      toast.success(t('settings.saved'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (error) {
    return (
      <TabsContent value="seo" className="mt-6">
        <Card className="p-6 sm:p-8">
          <CardContent className="py-2 text-sm text-muted-foreground">
            {t('settings.unavailable')}
          </CardContent>
        </Card>
      </TabsContent>
    )
  }

  if (isLoading || !form) {
    return (
      <TabsContent value="seo" className="mt-6 space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </TabsContent>
    )
  }

  const set = <K extends keyof SeoConfig>(key: K, value: SeoConfig[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f))

  const list = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean)

  return (
    <>
      <TabsContent value="seo" className="mt-6 space-y-6">
        <Card className="p-6 sm:p-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="size-4" />
              {t('settings.identity.title')}
            </CardTitle>
            <CardDescription>{t('settings.identity.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Field label={t('settings.siteName.label')} hint={t('settings.siteName.hint')}>
              <Input value={form.siteName} onChange={(e) => set('siteName', e.target.value)} />
            </Field>
            <Field label={t('settings.description.label')} hint={t('settings.description.hint')}>
              <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
            <Field label={t('settings.siteUrl.label')} hint={t('settings.siteUrl.hint')}>
              <Input placeholder="https://" value={form.siteUrl} onChange={(e) => set('siteUrl', e.target.value)} />
            </Field>
            <Field label={t('settings.keywords.label')} hint={t('settings.keywords.hint')}>
              <Input
                value={form.keywords.join(', ')}
                onChange={(e) => set('keywords', list(e.target.value))}
              />
            </Field>
            <Field label={t('settings.sameAs.label')} hint={t('settings.sameAs.hint')}>
              <Input
                placeholder="https://facebook.com/…, https://linkedin.com/…"
                value={form.sameAs.join(', ')}
                onChange={(e) => set('sameAs', list(e.target.value))}
              />
            </Field>
          </CardContent>
        </Card>
        <SaveBar pending={save.isPending} onSave={() => save.mutate(form)} />
      </TabsContent>

      <TabsContent value="ai" className="mt-6 space-y-6">
        <Card className="p-6 sm:p-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="size-4" />
              {t('settings.ai.title')}
            </CardTitle>
            <CardDescription>{t('settings.ai.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <Toggle
              checked={form.allowAiCrawlers}
              onChange={(v) => set('allowAiCrawlers', v)}
              title={t('settings.ai.allow.title')}
              body={t('settings.ai.allow.body')}
            />
            <Separator className="my-4" />
            <Toggle
              checked={form.prerenderForBots}
              onChange={(v) => set('prerenderForBots', v)}
              title={t('settings.ai.prerender.title')}
              body={t('settings.ai.prerender.body')}
            />
            <Separator className="my-4" />
            <div data-upbo-src="src/pages/Settings.tsx:154" className="space-y-2 pt-1">
              <p data-upbo-src="src/pages/Settings.tsx:155" className="text-sm font-medium">{t('settings.ai.filesLabel')}</p>
              <div data-upbo-src="src/pages/Settings.tsx:156" className="flex flex-wrap gap-2">
                {['/robots.txt', '/sitemap.xml', '/llms.txt', '/llms-full.txt'].map((f) => (
                  <a data-upbo-src="src/pages/Settings.tsx:158"
                    key={f}
                    href={f}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md border px-2.5 py-1 font-mono text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {f}
                  </a>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        <SaveBar pending={save.isPending} onSave={() => save.mutate(form)} />
      </TabsContent>
    </>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div data-upbo-src="src/pages/Settings.tsx:180" className="space-y-2">
      <Label>{label}</Label>
      {children}
      {hint && <p data-upbo-src="src/pages/Settings.tsx:183" className="text-xs leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  )
}

function Toggle({ checked, onChange, title, body }: { checked: boolean; onChange: (v: boolean) => void; title: string; body: string }) {
  return (
    <div data-upbo-src="src/pages/Settings.tsx:190" className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <div data-upbo-src="src/pages/Settings.tsx:191" className="space-y-1">
        <p data-upbo-src="src/pages/Settings.tsx:192" className="text-sm font-medium">{title}</p>
        <p data-upbo-src="src/pages/Settings.tsx:193" className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} className="mt-1 shrink-0" />
    </div>
  )
}

function SaveBar({ pending, onSave }: { pending: boolean; onSave: () => void }) {
  const { t } = useI18n()
  return (
    <div data-upbo-src="src/pages/Settings.tsx:203" className="flex justify-end">
      <Button onClick={onSave} disabled={pending}>
        {pending ? t('settings.saving') : <><Check className="size-4" />{t('settings.save')}</>}
      </Button>
    </div>
  )
}
